import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback } from 'react';
import { logger } from '../log';
import styles from './check.module.css';
import '@interlucent/pixel-stream';

const log = logger('check');

// Get API base URL for HTTP calls (convert wss:// to https://)
function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

// Session storage key for preview context (used by share dialog)
const PREVIEW_CONTEXT_KEY = 'preview_context';

interface PreviewContext {
  title?: string;
  projectId?: string;
  appId?: string;
  versionId?: string;
  admissionToken?: string;
  agentName?: string;
}

/**
 * Read and clear preview context from sessionStorage.
 */
function consumePreviewContext(): PreviewContext | null {
  try {
    const stored = sessionStorage.getItem(PREVIEW_CONTEXT_KEY);
    if (stored) {
      sessionStorage.removeItem(PREVIEW_CONTEXT_KEY);
      return JSON.parse(stored);
    }
  } catch {
    // sessionStorage might be unavailable
  }
  return null;
}

interface ShareResolution {
  admissionToken?: string;
  agentName?: string;
  requiresPasscode: boolean;
  passcodeProtected: boolean;
}

/**
 * Resolve a share ID to an admission token
 */
async function resolveShare(
  shareId: string,
  passcode?: string
): Promise<ShareResolution> {
  const apiBaseUrl = getApiBaseUrl();
  const res = await fetch(`${apiBaseUrl}/preview/share/${shareId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passcode }),
  });

  const data = await res.json();

  if (res.status === 401) {
    return {
      requiresPasscode: true,
      passcodeProtected: true,
    };
  }

  if (res.status === 410) {
    throw new Error('This preview link has expired.');
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || 'Failed to resolve share');
  }

  return {
    admissionToken: data.admissionToken,
    agentName: data.agentName,
    requiresPasscode: false,
    passcodeProtected: !!passcode,
  };
}

export const Route = createFileRoute('/check/$')({
  component: CheckRoute,
});

type TestStatus = 'pending' | 'running' | 'success' | 'warning' | 'error';

interface TestResult {
  name: string;
  status: TestStatus;
  message: string;
  details?: string[];
  duration?: number;
  timestamp?: number;
}

interface ConnectionStats {
  rtt?: number;
  packetsLost?: number;
  packetsSent?: number;
  packetsReceived?: number;
  bytesSent?: number;
  bytesReceived?: number;
  bitrate?: number;
  jitter?: number;
  localCandidateType?: string;
  remoteCandidateType?: string;
  connectionType?: string;
}

function CheckRoute() {
  const { _splat: shareId } = Route.useParams();
  const [tests, setTests] = useState<TestResult[]>([]);
  const [admissionToken, setAdmissionToken] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [passcodeRequired, setPasscodeRequired] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');
  const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [overallQuality, setOverallQuality] = useState<'excellent' | 'good' | 'fair' | 'poor' | null>(null);

  const pixelStreamRef = useRef<any>(null);
  const statsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const testStartTimeRef = useRef<number>(0);

  const addTest = useCallback((test: TestResult) => {
    setTests(prev => [...prev, { ...test, timestamp: performance.now() }]);
  }, []);

  const updateLastTest = useCallback((updates: Partial<TestResult>) => {
    setTests(prev => {
      if (prev.length === 0) return prev;
      const newTests = [...prev];
      newTests[newTests.length - 1] = { ...newTests[newTests.length - 1], ...updates };
      return newTests;
    });
  }, []);

  const getApiEndpoint = () => {
    if (typeof window === 'undefined') return 'api.interlucent.ai';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
      return 'api.dev.interlucent.ai';
    }
    if (hostname.includes('.dev.')) {
      return 'api.dev.interlucent.ai';
    }
    return 'api.interlucent.ai';
  };

  // Determine entry point and resolve share
  useEffect(() => {
    // Consume preview context from sessionStorage (for share dialog)
    const ctx = consumePreviewContext();
    if (ctx && ctx.admissionToken) {
      setAdmissionToken(ctx.admissionToken);
      return;
    }

    // Check URL params for direct token
    const params = new URLSearchParams(window.location.search);
    const directToken = params.get('token');
    if (directToken) {
      setAdmissionToken(directToken);
      return;
    }

    // Otherwise resolve share
    if (shareId) {
      resolveShareWithRetry(shareId);
    }
  }, [shareId]);

  const resolveShareWithRetry = async (id: string, passcode?: string) => {
    addTest({
      name: 'Resolving share link',
      status: 'running',
      message: 'Loading share configuration...',
    });

    try {
      const result = await resolveShare(id, passcode);

      if (result.requiresPasscode) {
        updateLastTest({
          status: 'warning',
          message: 'Share link requires passcode',
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
        setPasscodeRequired(true);
        return;
      }

      if (result.admissionToken) {
        updateLastTest({
          status: 'success',
          message: `Share resolved: ${result.agentName || 'Unknown agent'}`,
          details: [
            `Share ID: ${id}`,
            `Agent: ${result.agentName || 'N/A'}`,
            result.passcodeProtected ? 'Passcode: Protected' : 'Passcode: Not required',
          ],
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
        setAdmissionToken(result.admissionToken);
        setPasscodeRequired(false);
      } else {
        throw new Error('No admission token in response');
      }
    } catch (error) {
      updateLastTest({
        status: 'error',
        message: `Failed to resolve share: ${error instanceof Error ? error.message : String(error)}`,
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });
      setShareError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleSubmitPasscode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareId || !passcodeInput) return;
    resolveShareWithRetry(shareId, passcodeInput);
  };

  // Start diagnostics when admission token is available
  useEffect(() => {
    if (!admissionToken) return;

    testStartTimeRef.current = performance.now();

    // Browser compatibility test
    addTest({
      name: 'Browser compatibility',
      status: 'running',
      message: 'Checking WebRTC support...',
    });

    const hasRTC = typeof RTCPeerConnection !== 'undefined';
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasWebSocket = typeof WebSocket !== 'undefined';

    const browserName = detectBrowser();

    updateLastTest({
      status: hasRTC && hasWebSocket ? 'success' : 'error',
      message: hasRTC && hasWebSocket
        ? `${browserName} - WebRTC fully supported`
        : `${browserName} - Missing required WebRTC APIs`,
      details: [
        `RTCPeerConnection: ${hasRTC ? '✓' : '✗'}`,
        `getUserMedia: ${hasGetUserMedia ? '✓' : '✗'}`,
        `WebSocket: ${hasWebSocket ? '✓' : '✗'}`,
        `User Agent: ${navigator.userAgent.split(' ').pop() || 'Unknown'}`,
      ],
      duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
    });

    if (!hasRTC || !hasWebSocket) {
      setIsComplete(true);
      setOverallQuality('poor');
      return;
    }

    // Network connectivity
    addTest({
      name: 'Network connectivity',
      status: 'running',
      message: 'Testing internet connection...',
    });

    fetch('https://www.google.com/favicon.ico', { mode: 'no-cors', cache: 'no-cache' })
      .then(() => {
        const connection = (navigator as any).connection || (navigator as any).mozConnection;
        const details = [
          `Status: Online`,
        ];

        if (connection) {
          if (connection.effectiveType) details.push(`Connection type: ${connection.effectiveType}`);
          if (connection.downlink) details.push(`Downlink: ${connection.downlink} Mbps`);
          if (connection.rtt) details.push(`RTT: ${connection.rtt} ms`);
        }

        updateLastTest({
          status: 'success',
          message: 'Internet connection active',
          details,
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
      })
      .catch((error) => {
        updateLastTest({
          status: 'error',
          message: 'No internet connection detected',
          details: [`Error: ${error instanceof Error ? error.message : String(error)}`],
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
        setIsComplete(true);
        setOverallQuality('poor');
      });

    // API endpoint reachability
    addTest({
      name: 'API endpoint reachability',
      status: 'running',
      message: `Testing connection to ${getApiEndpoint()}...`,
    });

    const apiStart = performance.now();
    fetch(`https://${getApiEndpoint()}/health`, { cache: 'no-cache' })
      .then(() => {
        const latency = performance.now() - apiStart;
        updateLastTest({
          status: latency < 200 ? 'success' : 'warning',
          message: latency < 200
            ? `API endpoint reachable (${latency.toFixed(0)}ms)`
            : `API endpoint reachable but slow (${latency.toFixed(0)}ms)`,
          details: [
            `Endpoint: ${getApiEndpoint()}`,
            `Latency: ${latency.toFixed(0)}ms`,
          ],
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
      })
      .catch(() => {
        updateLastTest({
          status: 'error',
          message: `Cannot reach API endpoint: ${getApiEndpoint()}`,
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
        setIsComplete(true);
        setOverallQuality('poor');
      });

  }, [admissionToken, addTest, updateLastTest]);

  // pixel-stream event handlers
  const handleSessionStateChange = useCallback((e: CustomEvent) => {
    const { newState } = e.detail;

    switch (newState) {
      case 'connecting':
        addTest({
          name: 'Admission token exchange',
          status: 'running',
          message: 'Exchanging admission token for access token...',
        });
        break;

      case 'authenticating':
        updateLastTest({
          status: 'success',
          message: 'Admission token exchanged successfully',
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });

        addTest({
          name: 'Session WebSocket connection',
          status: 'running',
          message: `Connecting to wss://${getApiEndpoint()}/ws/session...`,
        });
        break;

      case 'connected':
        // Will be updated when session-ready is received
        break;

      case 'error':
        updateLastTest({
          status: 'error',
          message: 'Session connection failed',
          duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
        });
        setIsComplete(true);
        setOverallQuality('poor');
        break;
    }
  }, [addTest, updateLastTest]);

  const handleSessionReady = useCallback((e: CustomEvent) => {
    const { sessionId, agentId, participants } = e.detail;

    updateLastTest({
      status: 'success',
      message: 'Session WebSocket connected',
      details: [
        `Session ID: ${sessionId}`,
        `Agent ID: ${agentId}`,
        `Participants: ${participants?.length || 0}`,
      ],
      duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
    });

    addTest({
      name: 'Waiting for worker',
      status: 'running',
      message: 'Waiting for worker to join session...',
    });
  }, [updateLastTest, addTest]);

  const handleParticipantJoined = useCallback((e: CustomEvent) => {
    const { agentId } = e.detail;

    updateLastTest({
      status: 'success',
      message: 'Worker joined session',
      details: [
        `Worker agent ID: ${agentId}`,
      ],
      duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
    });

    addTest({
      name: 'WebRTC connection',
      status: 'running',
      message: 'Establishing WebRTC peer connection...',
    });
  }, [updateLastTest, addTest]);

  const handleConnectionStateChange = useCallback((e: CustomEvent) => {
    const state = e.detail;

    if (state === 'connected') {
      updateLastTest({
        status: 'success',
        message: 'WebRTC connection established',
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });

      // Start collecting stats
      startStatsCollection();

      addTest({
        name: 'Media tracks',
        status: 'running',
        message: 'Checking media tracks...',
      });
    } else if (state === 'failed') {
      updateLastTest({
        status: 'error',
        message: 'WebRTC connection failed',
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });
      setIsComplete(true);
      setOverallQuality('poor');
    } else if (state === 'disconnected') {
      updateLastTest({
        status: 'warning',
        message: 'WebRTC connection disconnected',
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });
    }
  }, [updateLastTest, addTest]);

  const handleSessionError = useCallback((e: CustomEvent) => {
    const { error, phase } = e.detail;

    addTest({
      name: `Error during ${phase}`,
      status: 'error',
      message: error,
    });
  }, [addTest]);

  const handleIceCandidateError = useCallback((e: CustomEvent) => {
    const { errorCode, errorText, url } = e.detail;

    addTest({
      name: 'ICE candidate error',
      status: 'warning',
      message: `ICE error: ${errorText}`,
      details: [
        `Error code: ${errorCode}`,
        `URL: ${url || 'N/A'}`,
      ],
    });
  }, [addTest]);

  const handleSessionEnded = useCallback((e: CustomEvent) => {
    const { reason } = e.detail || {};

    if (reason === 'rendezvous-timeout') {
      updateLastTest({
        status: 'warning',
        message: 'No worker available',
        details: [
          'Session established successfully',
          'No worker joined within timeout period',
          'This indicates no workers in the pool or insufficient capacity',
        ],
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });
      setIsComplete(true);
      setOverallQuality('fair');
    } else {
      updateLastTest({
        status: 'error',
        message: `Session ended: ${reason || 'Unknown reason'}`,
        duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
      });
      setIsComplete(true);
      setOverallQuality('poor');
    }
  }, [updateLastTest]);

  const startStatsCollection = useCallback(() => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }

    const el = pixelStreamRef.current;
    if (!el) return;

    // Access the peer connection (pixel-stream exposes it)
    const pc = (el as any).peerConnection;
    if (!pc) return;

    let statsCollected = 0;
    const maxStats = 10; // Collect for 10 seconds

    statsIntervalRef.current = setInterval(async () => {
      try {
        const stats = await pc.getStats();
        const parsedStats = parseWebRTCStats(stats);
        setConnectionStats(parsedStats);

        statsCollected++;

        if (statsCollected === 1) {
          // First stat collection - update media tracks test
          updateLastTest({
            status: 'success',
            message: parsedStats.videoTrack
              ? `Receiving video (${parsedStats.videoWidth}x${parsedStats.videoHeight})`
              : 'Video track available',
            details: [
              parsedStats.videoTrack ? `Video: ${parsedStats.videoWidth}x${parsedStats.videoHeight} @ ${parsedStats.videoFrameRate}fps` : 'Video: Available',
              parsedStats.audioTrack ? 'Audio: Receiving' : 'Audio: Available',
            ],
            duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
          });

          addTest({
            name: 'Connection stability',
            status: 'running',
            message: 'Monitoring connection quality (10s)...',
          });
        }

        if (statsCollected >= maxStats) {
          clearInterval(statsIntervalRef.current!);
          statsIntervalRef.current = null;

          // Final assessment
          const quality = assessConnectionQuality(parsedStats);
          setOverallQuality(quality);

          updateLastTest({
            status: quality === 'excellent' || quality === 'good' ? 'success' : 'warning',
            message: `Connection ${quality}`,
            details: [
              `RTT: ${parsedStats.rtt?.toFixed(0)}ms`,
              `Packet loss: ${((parsedStats.packetsLost || 0) / (parsedStats.packetsSent || 1) * 100).toFixed(2)}%`,
              `Bitrate: ${(parsedStats.bitrate! / 1_000_000).toFixed(2)} Mbps`,
              `Connection type: ${parsedStats.connectionType || 'Unknown'}`,
            ],
            duration: performance.now() - (tests[tests.length - 1]?.timestamp || 0),
          });

          setIsComplete(true);
        }
      } catch (error) {
        log.error('error collecting stats', error);
      }
    }, 1000);
  }, [updateLastTest, addTest]);

  // Attach event listeners
  useEffect(() => {
    const el = pixelStreamRef.current;
    if (!el) return;

    el.addEventListener('session-state-change', handleSessionStateChange);
    el.addEventListener('session-ready', handleSessionReady);
    el.addEventListener('participant-joined', handleParticipantJoined);
    el.addEventListener('connection-state-change', handleConnectionStateChange);
    el.addEventListener('session-error', handleSessionError);
    el.addEventListener('ice-candidate-error', handleIceCandidateError);
    el.addEventListener('session-ended', handleSessionEnded);

    // Start session after listeners are attached
    if ((el as any).startSession) {
      (el as any).startSession();
    }

    return () => {
      el.removeEventListener('session-state-change', handleSessionStateChange);
      el.removeEventListener('session-ready', handleSessionReady);
      el.removeEventListener('participant-joined', handleParticipantJoined);
      el.removeEventListener('connection-state-change', handleConnectionStateChange);
      el.removeEventListener('session-error', handleSessionError);
      el.removeEventListener('ice-candidate-error', handleIceCandidateError);
      el.removeEventListener('session-ended', handleSessionEnded);

      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }

      // End session when component unmounts
      if ((el as any).stop) {
        (el as any).stop();
      }
    };
  }, [admissionToken, handleSessionStateChange, handleSessionReady, handleParticipantJoined, handleConnectionStateChange, handleSessionError, handleIceCandidateError, handleSessionEnded]);

  const detectBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  };

  const parseWebRTCStats = (stats: RTCStatsReport): any => {
    const result: any = {};

    stats.forEach((stat) => {
      if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
        result.rtt = stat.currentRoundTripTime ? stat.currentRoundTripTime * 1000 : undefined;
        result.bytesSent = stat.bytesSent;
        result.bytesReceived = stat.bytesReceived;
        result.localCandidateType = 'unknown';
        result.remoteCandidateType = 'unknown';
      }

      if (stat.type === 'local-candidate' && stat.id === result.localCandidateId) {
        result.localCandidateType = stat.candidateType;
      }

      if (stat.type === 'remote-candidate' && stat.id === result.remoteCandidateId) {
        result.remoteCandidateType = stat.candidateType;
      }

      if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
        result.videoTrack = true;
        result.packetsLost = stat.packetsLost;
        result.packetsReceived = stat.packetsReceived;
        result.jitter = stat.jitter;
        result.videoWidth = stat.frameWidth;
        result.videoHeight = stat.frameHeight;
        result.videoFrameRate = stat.framesPerSecond;

        if (stat.bytesReceived && result.lastBytesReceived) {
          const bytesDiff = stat.bytesReceived - result.lastBytesReceived;
          result.bitrate = bytesDiff * 8; // bits per second
        }
        result.lastBytesReceived = stat.bytesReceived;
      }

      if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
        result.audioTrack = true;
      }

      if (stat.type === 'transport') {
        result.packetsSent = stat.packetsSent;
      }
    });

    // Determine connection type
    if (result.localCandidateType === 'relay' || result.remoteCandidateType === 'relay') {
      result.connectionType = 'relay (TURN)';
    } else if (result.localCandidateType === 'srflx' || result.remoteCandidateType === 'srflx') {
      result.connectionType = 'server-reflexive';
    } else if (result.localCandidateType === 'host' && result.remoteCandidateType === 'host') {
      result.connectionType = 'direct (host)';
    }

    return result;
  };

  const assessConnectionQuality = (stats: ConnectionStats): 'excellent' | 'good' | 'fair' | 'poor' => {
    const rtt = stats.rtt || 0;
    const packetLoss = ((stats.packetsLost || 0) / (stats.packetsSent || 1)) * 100;

    if (rtt < 50 && packetLoss < 0.1) return 'excellent';
    if (rtt < 100 && packetLoss < 1) return 'good';
    if (rtt < 200 && packetLoss < 3) return 'fair';
    return 'poor';
  };

  const getStatusIcon = (status: TestStatus) => {
    switch (status) {
      case 'pending': return '⏱️';
      case 'running': return '⏳';
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
    }
  };

  const getQualityStars = (quality: string | null) => {
    switch (quality) {
      case 'excellent': return '⭐⭐⭐⭐⭐';
      case 'good': return '⭐⭐⭐⭐';
      case 'fair': return '⭐⭐⭐';
      case 'poor': return '⭐';
      default: return '';
    }
  };

  const copyResults = () => {
    const text = tests.map(test => {
      let result = `${getStatusIcon(test.status)} ${test.name}\n`;
      result += `   ${test.message}\n`;
      if (test.details && test.details.length > 0) {
        result += test.details.map(d => `   ${d}`).join('\n') + '\n';
      }
      if (test.duration) {
        result += `   Duration: ${test.duration.toFixed(0)}ms\n`;
      }
      return result;
    }).join('\n');

    const summary = `\nConnection Quality: ${getQualityStars(overallQuality)}\n`;
    const finalText = text + summary;

    navigator.clipboard.writeText(finalText);
  };

  const startStreaming = () => {
    window.location.href = `/preview/${shareId}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Connection Diagnostic</h1>
        <p>Testing your connectivity to Interlucent streaming infrastructure</p>
        {shareId && <p className={styles.shareId}>Share ID: {shareId}</p>}
      </div>

      {shareError && (
        <div className={styles.error}>
          <h3>Share Resolution Error</h3>
          <p>{shareError}</p>
        </div>
      )}

      {passcodeRequired && (
        <div className={styles.passcodeForm}>
          <h3>Passcode Required</h3>
          <form onSubmit={handleSubmitPasscode}>
            <input
              type="password"
              value={passcodeInput}
              onChange={(e) => setPasscodeInput(e.target.value)}
              placeholder="Enter passcode"
              className={styles.passcodeInput}
              autoFocus
            />
            <button type="submit" className={styles.submitButton}>
              Submit
            </button>
          </form>
        </div>
      )}

      <div className={styles.tests}>
        {tests.map((test, i) => (
          <div key={i} className={`${styles.test} ${styles[test.status]}`}>
            <div className={styles.testHeader}>
              <span className={styles.testIcon}>{getStatusIcon(test.status)}</span>
              <h3>{test.name}</h3>
              {test.duration !== undefined && (
                <span className={styles.duration}>{test.duration.toFixed(0)}ms</span>
              )}
            </div>
            <p className={styles.message}>{test.message}</p>
            {test.details && test.details.length > 0 && (
              <div className={styles.details}>
                {test.details.map((detail, j) => (
                  <div key={j} className={styles.detail}>{detail}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {isComplete && (
        <div className={`${styles.summary} ${styles[overallQuality || 'fair']}`}>
          <h2>Diagnostic Complete</h2>
          <div className={styles.qualityRating}>
            <div className={styles.stars}>{getQualityStars(overallQuality)}</div>
            <div className={styles.qualityLabel}>
              {overallQuality === 'excellent' && 'Excellent Connection'}
              {overallQuality === 'good' && 'Good Connection'}
              {overallQuality === 'fair' && 'Fair Connection'}
              {overallQuality === 'poor' && 'Poor Connection'}
            </div>
          </div>

          {connectionStats && (
            <div className={styles.statsGrid}>
              {connectionStats.rtt !== undefined && (
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Latency</div>
                  <div className={styles.statValue}>{connectionStats.rtt.toFixed(0)}ms</div>
                </div>
              )}
              {connectionStats.bitrate !== undefined && (
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Bitrate</div>
                  <div className={styles.statValue}>{(connectionStats.bitrate / 1_000_000).toFixed(2)} Mbps</div>
                </div>
              )}
              {connectionStats.packetsLost !== undefined && connectionStats.packetsSent !== undefined && (
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Packet Loss</div>
                  <div className={styles.statValue}>
                    {((connectionStats.packetsLost / connectionStats.packetsSent) * 100).toFixed(2)}%
                  </div>
                </div>
              )}
              {connectionStats.connectionType && (
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Connection Type</div>
                  <div className={styles.statValue}>{connectionStats.connectionType}</div>
                </div>
              )}
            </div>
          )}

          <div className={styles.actions}>
            <button onClick={copyResults} className={styles.copyButton}>
              Copy Results
            </button>
            {overallQuality !== 'poor' && (
              <button onClick={startStreaming} className={styles.streamButton}>
                Start Streaming
              </button>
            )}
          </div>

          {overallQuality === 'poor' && (
            <div className={styles.recommendations}>
              <h3>Recommendations</h3>
              <ul>
                <li>Check your internet connection</li>
                <li>Try a different network (disable VPN if active)</li>
                <li>Ensure firewall allows WebRTC traffic (UDP preferred)</li>
                <li>Contact support with the diagnostic results</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Hidden pixel-stream doing the real work */}
      {admissionToken && !shareError && (
        <pixel-stream
          ref={pixelStreamRef}
          admission-token={admissionToken}
          api-endpoint={getApiEndpoint()}
          reconnect-mode="none"
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
}
